<template>
  <slot v-if="!error" />
  <div v-else class="error-boundary" role="alert">
    <div class="error-boundary__inner glass-card">
      <AlertTriangle class="error-boundary__icon" />
      <h2 class="error-boundary__title">Something went wrong</h2>
      <p class="error-boundary__msg">{{ error.message || 'An unexpected error occurred.' }}</p>
      <details v-if="error.stack" class="error-boundary__details">
        <summary>Stack trace</summary>
        <pre>{{ error.stack }}</pre>
      </details>
      <div class="error-boundary__actions">
        <PrimaryButton @click="reset">
          <RefreshCw class="w-4 h-4" />
          Try again
        </PrimaryButton>
        <button class="btn-ghost" @click="goHome">Go to home</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onErrorCaptured, ref } from 'vue';
import { useRouter } from 'vue-router';
import { AlertTriangle, RefreshCw } from '@lucide/vue';
import PrimaryButton from './PrimaryButton.vue';

const error = ref<Error | null>(null);
const router = useRouter();

onErrorCaptured((err: unknown) => {
  error.value = err instanceof Error ? err : new Error(String(err));
  // eslint-disable-next-line no-console
  console.error('[ErrorBoundary]', err);
  return false;
});

function reset() {
  error.value = null;
  // Reload the current route
  router.go(0);
}

function goHome() {
  error.value = null;
  router.push('/');
}
</script>

<style scoped>
.error-boundary {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.error-boundary__inner {
  max-width: 480px;
  width: 100%;
  padding: 32px;
  text-align: center;
}
.error-boundary__icon {
  width: 48px;
  height: 48px;
  color: var(--color-warning, #f59e0b);
  margin: 0 auto 16px;
}
.error-boundary__title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--color-text-primary);
}
.error-boundary__msg {
  color: var(--color-text-secondary);
  margin-bottom: 20px;
  font-size: 14px;
}
.error-boundary__details {
  text-align: left;
  margin-bottom: 20px;
  font-size: 12px;
}
.error-boundary__details summary {
  cursor: pointer;
  color: var(--color-text-muted);
  user-select: none;
}
.error-boundary__details pre {
  margin-top: 8px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 8px;
  overflow-x: auto;
  color: var(--color-text-secondary);
  white-space: pre-wrap;
  word-break: break-word;
}
.error-boundary__actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}
</style>